<script lang="ts">
import { defineBlock } from "@bagelink/blox"
import { $, defineSchema } from "@bagelink/vue"
const schema = defineSchema({
  anchor: $.text("Section anchor", {
    default: "",
    placeholder: "Optional",
  }),
  title: $.text("Heading", { default: "With gratitude" }),
  specialHeading: $.checkbox("Use special heading font", { default: true }),
  intro: $.text("Introduction", { multiline: true }),
  layout: $.select(
    "Layout",
    [
      { label: "Wrapped with separators", value: "inline" },
      { label: "Columns", value: "columns" },
      { label: "Separate groups", value: "groups" },
    ],
    { default: "inline" },
  ),
  columns: $.select(
    "Columns",
    [
      { label: "Two", value: 2 },
      { label: "Three", value: 3 },
      { label: "Four", value: 4 },
    ],
    { default: 3 },
  ).if('layout eq "columns" or layout eq "groups"'),
  separator: $.text("Separator", { default: "|" }).if('layout eq "inline"'),
  surface: $.select(
    "Background",
    [
      { label: "Page background", value: "page" },
      { label: "White background", value: "white" },
      { label: "White card", value: "card" },
      { label: "Brand color", value: "brand" },
      { label: "Dark", value: "dark" },
      { label: "Image", value: "image" },
    ],
    { default: "page" },
  ),
  backgroundImage: $.upload("Background image").if(
    'surface eq "image" or surface eq "card"',
  ),
  backgroundFit: $.select(
    "Background image fit",
    [
      { label: "Cover", value: "cover" },
      { label: "Contain", value: "contain" },
      { label: "Fill", value: "fill" },
    ],
    { default: "cover" },
  ).if(
    '(surface eq "image" or surface eq "card") and backgroundImage pr',
  ),
  dimBackground: $.checkbox("Dim background", { default: false }).if(
    '(surface eq "image" or surface eq "card") and backgroundImage pr',
  ),
  align: $.select(
    "Alignment",
    [
      { label: "Center", value: "center" },
      { label: "Start", value: "start" },
    ],
    { default: "center" },
  ),
  largeText: $.checkbox("Large name text", { default: false }),
  compact: $.checkbox("Compact spacing", { default: false }),
  collapsible: $.checkbox("Collapse long lists", { default: false }),
  initialCount: $.number("Names shown before expansion", {
    default: 18,
    min: 1,
  }).if("collapsible eq true"),
  namesText: $.text("Names", {
    multiline: true,
    rows: 10,
    autoheight: true,
    placeholder: "Enter one name per line, or separate names with commas",
  }).if('layout ne "groups"'),
  groups: $.array(
    "Groups",
    defineSchema({
      title: $.text("Group heading"),
      namesText: $.text("Names", {
        multiline: true,
        rows: 10,
        autoheight: true,
        placeholder: "Enter one name per line, or separate names with commas",
      }),
    }),
    {
      collapsible: true,
      allowAdd: true,
      allowDelete: true,
      allowReorder: true,
      default: [{ title: "", namesText: "" }],
    },
  ).if('layout eq "groups"'),
  footer: $.text("Footer note", { multiline: true }),
});
export const EventNames = defineBlock({
  name: "EventNames",
  label: "Name list",
  icon: "format_list_bulleted",
  thumbnail: "/block-thumbs/EventNames.svg",
  category: "Event",
  order: 40,
  schema,
})
</script>
<script setup lang="ts">
import { computed, ref } from "vue";

import { Btn, Icon, pathKeyToURL } from "@bagelink/vue"

type NameEntry = { name: string; note?: string };
type NameGroup = { title?: string; namesText?: string; names?: NameEntry[] };


;

const props = withDefaults(
  defineProps<{
    anchor?: string;
    title?: string;
    specialHeading?: boolean;
    intro?: string;
    layout?: "inline" | "columns" | "groups";
    columns?: number;
    separator?: string;
    surface?: "page" | "white" | "card" | "brand" | "dark" | "image";
    backgroundImage?: string;
    backgroundFit?: "cover" | "contain" | "fill";
    dimBackground?: boolean;
    align?: "center" | "start";
    largeText?: boolean;
    compact?: boolean;
    collapsible?: boolean;
    initialCount?: number;
    namesText?: string;
    groups?: NameGroup[];
    footer?: string;
  }>(),
  {
    anchor: "",
    title: "With gratitude",
    specialHeading: true,
    intro: "",
    layout: "inline",
    columns: 3,
    separator: "|",
    surface: "page",
    backgroundImage: "",
    backgroundFit: "cover",
    dimBackground: false,
    align: "center",
    largeText: false,
    compact: false,
    collapsible: false,
    initialCount: 18,
    namesText: "",
    groups: () => [],
    footer: "",
  },
);

const expanded = ref(false);
function parseNames(value?: string): NameEntry[] {
  return (value ?? "")
    .split(/[,\n]+/)
    .map((name) => name.trim())
    .filter(Boolean)
    .map((name) => ({ name }));
}

function groupNames(group: NameGroup): NameEntry[] {
  const parsed = parseNames(group.namesText);
  return parsed.length
    ? parsed
    : (group.names ?? []).filter((entry) => entry.name?.trim());
}

const enteredGroups = computed<NameGroup[]>(() =>
  props.layout === "groups"
    ? props.groups.map((group) => ({ ...group, names: groupNames(group) }))
    : [
        {
          names: parseNames(props.namesText).length
            ? parseNames(props.namesText)
            : props.groups.flatMap(groupNames),
        },
      ],
);
const hasNames = computed(() =>
  enteredGroups.value.some((group) => (group.names ?? []).length > 0),
);
const displayedGroups = computed<NameGroup[]>(() => enteredGroups.value);
const allNames = computed(() =>
  displayedGroups.value.flatMap((group) => group.names ?? []),
);
const visibleGroups = computed<NameGroup[]>(() => {
  if (!props.collapsible || expanded.value) return displayedGroups.value;
  let remaining = props.initialCount;
  return displayedGroups.value.flatMap((group) => {
    if (remaining <= 0) return [];
    const names = (group.names ?? []).slice(0, remaining);
    remaining -= names.length;
    return names.length ? [{ ...group, names }] : [];
  });
});
const visibleNames = computed(() =>
  props.collapsible && !expanded.value
    ? allNames.value.slice(0, props.initialCount)
    : allNames.value,
);
const hiddenCount = computed(() =>
  Math.max(0, allNames.value.length - props.initialCount),
);
const sectionClass = computed(
  () =>
    ({
      page: "bg-transparent",
      white: "bg-box",
      card: props.backgroundImage ? "" : "bg-bg",
      brand: "bg-primary color-white keep-white",
      dark: "bg-black color-white keep-white",
      image: "",
    })[props.surface],
);
const contentClass = computed(() => [
  props.surface === "card"
    ? "bg-box shadow-light radius-2 p-2 m_p-1"
    : "",
  props.align === "center" ? "txt-center" : "txt-start",
]);
const backgroundStyle = computed(() =>
  props.backgroundImage
    ? {
        backgroundImage:
          props.surface === "card"
            ? `url("${pathKeyToURL(props.backgroundImage) || props.backgroundImage}")`
            : `${props.dimBackground ? "linear-gradient(#0006, #0006), " : ""}url("${pathKeyToURL(props.backgroundImage) || props.backgroundImage}")`,
        backgroundSize:
          props.backgroundFit === "fill"
            ? "100% 100%"
            : props.backgroundFit,
      }
    : undefined,
);
const sectionStyle = computed(() =>
  props.surface === "image" || props.surface === "card"
    ? backgroundStyle.value
    : undefined,
);
const columnClass = computed(
  () => `grid grid-wrap-${props.columns} gap-075 m_gap-05`,
);
const namesTextClass = computed(() =>
  props.largeText ? "txt-24 m_txt-18" : "txt-20",
);
</script>

<template>
  <section
    :id="anchor || undefined"
    class="blox-background-image px-2 m_px-1 relative"
    :class="[sectionClass, compact ? 'py-2 m_py-1' : 'py-5 m_py-2']"
    :style="sectionStyle"
  >
    <div class="w-1000px" :class="contentClass">
      <h2
        v-if="title"
        class="m-0 txt-70 m_txt-40 line-height-1 m_line-height-11 balance pb-1"
        :class="{ 'blox-special-heading': specialHeading }"
      >
        {{ title }}
      </h2>
      <p v-if="intro" class="pretty pb-2">{{ intro }}</p>

      <TransitionGroup
        v-if="layout === 'groups' && hasNames"
        name="name-list"
        tag="div"
        class="grid gap-2 m_gap-1"
      >
        <section
          v-for="(group, groupIndex) in visibleGroups"
          :key="`${group.title}-${groupIndex}`"
        >
          <h3
            v-if="group.title"
            class="m-0 line-height-12 txt-28 m_txt-24 balance pb-075"
          >
            {{ group.title }}
          </h3>
          <div :class="[columnClass, namesTextClass]">
            <p
              v-for="(entry, index) in group.names"
              :key="`${entry.name}-${index}`"
              class="m-0"
            >
              <strong>{{ entry.name }}</strong>
              <span v-if="entry.note" class="block txt14 opacity-7">{{
                entry.note
              }}</span>
            </p>
          </div>
        </section>
      </TransitionGroup>

      <TransitionGroup
        v-else-if="layout === 'columns' && hasNames"
        name="name-list"
        tag="div"
        :class="[columnClass, namesTextClass]"
      >
        <p
          v-for="(entry, index) in visibleNames"
          :key="`${entry.name}-${index}`"
          class="m-0"
        >
          <strong>{{ entry.name }}</strong>
          <span v-if="entry.note" class="block txt14 opacity-7">{{
            entry.note
          }}</span>
        </p>
      </TransitionGroup>

      <TransitionGroup
        v-else-if="hasNames"
        name="name-list"
        tag="p"
        class="m-0"
        :class="namesTextClass"
      >
        <span
          v-for="(entry, index) in visibleNames"
          :key="`${entry.name}-${index}`"
          class="inline-block"
        >
          <span :class="[namesTextClass]">{{ entry.name }}</span>
          <span v-if="entry.note"> — {{ entry.note }}</span>
          <span
            v-if="separator && index < visibleNames.length - 1"
            class="mx-075"
            aria-hidden="true"
            >{{ separator }}</span
          >
        </span>
      </TransitionGroup>

      <Btn
        v-if="collapsible && hiddenCount > 0"
        flat
        class="mt-1"
        @click="expanded = !expanded"
      >
        <span>{{ expanded ? "Show fewer" : `Show ${hiddenCount} more` }}</span>
        <Icon
          name="expand_more"
          class="transition-400"
          :class="{ 'rotate-180': expanded }"
        />
      </Btn>
      <p v-if="footer" class="m-0 pt-2 pretty">{{ footer }}</p>
    </div>
  </section>
</template>

<style scoped>
.name-list-enter-active,
.name-list-leave-active,
.name-list-move {
  transition:
    opacity 300ms ease,
    transform 300ms ease;
}
.name-list-enter-from,
.name-list-leave-to {
  opacity: 0;
  transform: translateY(-0.5rem);
}

@media (prefers-reduced-motion: reduce) {
  .name-list-enter-active,
  .name-list-leave-active,
  .name-list-move {
    transition: none;
  }
}
</style>
